import { useState, useEffect, useRef } from 'react'
import { ChevronDown, ChevronLeft, Calendar, Users, ListFilter, Briefcase, 
  UserCircle, Filter, Clock, TimerIcon, Building2, Layers, 
  Target, ListChecks, BookMarked, MapPin, UserCheck, ThumbsUp, MessageCircle, PhoneCall, Save, X, PhoneForwarded, BarChart3 } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { CalendarComponent } from "../../components/Calendar";
import { DateOptions, TranscriptDateOptions, updateAnalyticsDateRange, updateTranscriptDateFilter, updateHistoryFilter, FilterContext, updateAnalyticsFilter } from '../../lib/redux/filters/slice'
import { SimplifiedSingleSelect } from '../../components/Selectors/SimplifiedSingleSelect'
import React from 'react'
import { MultiUserSelect, MultiUserSelector } from '../Selectors/MulltiUserSelector'
import { FilterColumnRow } from '../BackendFilterSelector/FilterColumnRow'
import { FilterColumn } from '../../interfaces/sessions'
import { SESSION_METRICS_COLUMNS, PROSPECT_INFO_COLUMNS, SessionAgentsColumns, BackendColumns, CallMetricColumns, CustomMetricColumns } from '../../interfaces/table'
import { getColumnType, getDefaultOperator, FilterColumnTypes } from '../BackendFilterSelector/cfg'
import { BackendTables, CustomMetricValueType } from '../../interfaces/db'
import { FilterOperator } from '../../interfaces/sessions'
import { useColumnOptions } from '../../lib/redux/filters/options/options-hook'
import { useSelectedRepsFromFilter } from '../../lib/redux/filters/utils'
import { useUserData } from '../../lib/redux/users/user-data-hook'
import { SavedViewModal } from '../SavedViews'
import { openEditingModal, closeEditingModal } from '../../lib/redux/views/slice'

export enum SPECIAL_FILTERS {
  SELECTED_REPS = 'Agents',
  TIMEZONE = 'Timezone',
  ANALYTICS_DATE = 'Analytics Date',
  TRANSCRIPT_DATE = 'Transcript Date',
  CUSTOM_METRICS = 'Custom Metrics'
}

interface FilterProps {
  isOpen: boolean
  onClose: () => void
  special_filters: SPECIAL_FILTERS[]
  applicable_filters: BackendColumns[]
  filterContext: FilterContext
}

export function StaticFilterComponent(props: FilterProps) {
  const { isOpen, onClose, special_filters, applicable_filters, filterContext } = props
  const dispatch = useDispatch()
  
  // Conditionally select the appropriate filter state based on context
  const historyFilter = useSelector((state: RootState) => state.historyFilter.value)
  const analyticsFilter = useSelector((state: RootState) => state.analyticsFilter.value)
  const filter = filterContext === FilterContext.ANALYTICS ? analyticsFilter : historyFilter
  
  const analyticsDateFilter = useSelector((state: RootState) => state.analyticsDateFilter.analytics)
  const transcriptDateFilter = useSelector((state: RootState) => state.transcriptDateFilter)
  
  // Get user data from centralized hook
  const { allVisibleUsers, sortedUserGroupInfo, customDefinitions } = useUserData()
  
  // Saved Views Redux state
  const isEditingModalOpen = useSelector((state: RootState) => state.savedViews.isEditingModalOpen)
  const editingView = useSelector((state: RootState) => state.savedViews.editingView)
  const savedViews = useSelector((state: RootState) => state.savedViews.value) || []
  
  const [expandedSections, setExpandedSections] = useState<{[k: string]: boolean}>({...Object.fromEntries(Object.values(SPECIAL_FILTERS).map((filter) => [filter, true])), 'SELECTED_REPS': true})
  const [isVisible, setIsVisible] = useState(isOpen)
  const [isTransitioning, setIsTransitioning] = useState(false)

  const { hasOptions } = useColumnOptions()
  const selectedReps = useSelectedRepsFromFilter(filter)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true)
      setIsTransitioning(true)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => setIsTransitioning(false), 300)
    } else {
      setIsTransitioning(true)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => {
        setIsVisible(false)
        setIsTransitioning(false)
      }, 300)
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [isOpen])

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const handleSaveView = () => {
    // Open modal for creating new view with current filter
    dispatch(openEditingModal(null))
  }

  const getIconForColumn = (column: BackendColumns): React.ReactNode => {
    // Check if this is a custom metric column first
    if (isCustomMetricColumn(column)) {
      return <BarChart3 className="w-4 h-4" />;
    }
    
    switch (column) {
      case SESSION_METRICS_COLUMNS.STAGE: return <Layers className="w-4 h-4" />
      case SESSION_METRICS_COLUMNS.IS_ANSWERED: return <PhoneCall className="w-4 h-4" />
      case SESSION_METRICS_COLUMNS.CALL_DURATION: return <TimerIcon className="w-4 h-4" />
      case SESSION_METRICS_COLUMNS.PLATFORM: return <ListFilter className="w-4 h-4" />
      case SESSION_METRICS_COLUMNS.BEST_COUNTERPART: return <UserCircle className="w-4 h-4" />
      case SESSION_METRICS_COLUMNS.USER_DISPOSITION: return <UserCheck className="w-4 h-4" />
      case SESSION_METRICS_COLUMNS.USER_SENTIMENT: return <ThumbsUp className="w-4 h-4" />
      case SESSION_METRICS_COLUMNS.USER_PURPOSE: return <Target className="w-4 h-4" />
      case SESSION_METRICS_COLUMNS.IS_CONNECTED: return <PhoneCall className="w-4 h-4" />
      case SESSION_METRICS_COLUMNS.TRELLUS_DISPOSITION: return <MessageCircle className="w-4 h-4" />
      case CallMetricColumns.IS_INBOUND: return <PhoneForwarded className="w-4 h-4" />
      case SESSION_METRICS_COLUMNS.LIVE_DURATION: return <Clock className="w-4 h-4" />
      case PROSPECT_INFO_COLUMNS.CADENCE: return <ListChecks className="w-4 h-4" />
      case PROSPECT_INFO_COLUMNS.COMPANY_INDUSTRY: return <Building2 className="w-4 h-4" />
      case PROSPECT_INFO_COLUMNS.PROSPECT_TITLE: return <UserCircle className="w-4 h-4" />
      case PROSPECT_INFO_COLUMNS.PROSPECT_LIST: return <BookMarked className="w-4 h-4" />
      case PROSPECT_INFO_COLUMNS.MARKET: return <MapPin className="w-4 h-4" />
      case PROSPECT_INFO_COLUMNS.LEAD_TYPE: return <Briefcase className="w-4 h-4" />
      default: return <Filter className="w-4 h-4" />
    }
  }

  // Helper function to check if a column is a custom metric
  const isCustomMetricColumn = (columnName: string): boolean => {
    return Object.values(CustomMetricColumns).includes(columnName as CustomMetricColumns) && 
           columnName !== CustomMetricColumns.SESSION_ID && 
           !columnName.startsWith('text_');
  };

  // Helper function to get custom metric definition
  const getCustomMetricDefinition = (columnName: string) => {
    if (!isCustomMetricColumn(columnName)) return null;
    
    // Extract metric index from column name (e.g., "metric_0" -> 0)
    const match = columnName.match(/^metric_(\d+)$/);
    if (!match) return null;
    
    const metricIdx = parseInt(match[1], 10);
    return customDefinitions.find(def => def.metric_idx === metricIdx);
  };

  // Get filterable custom metrics
  const getFilterableCustomMetrics = () => {
    return customDefinitions
      .filter(def => 
        def.value_type !== CustomMetricValueType.TEXT && 
        def.metric_idx !== null
      )
      .map(def => {
        const metricColumnName = `METRIC_${def.metric_idx}` as keyof typeof CustomMetricColumns;
        const columnName = CustomMetricColumns[metricColumnName];
        return {
          definition: def,
          columnName: columnName as string
        };
      })
      .filter(item => item.columnName); // Only include valid column names
  };

  // Convert column to FilterColumn for use with FilterColumnRow
  const createFilterColumn = (column: BackendColumns): FilterColumn => {
    let columnType = getColumnType(column)
    
    // Override column type for custom metrics based on their definition
    if (isCustomMetricColumn(column)) {
      const definition = getCustomMetricDefinition(column)
      if (definition) {
        switch (definition.value_type) {
          case CustomMetricValueType.BOOLEAN:
            columnType = FilterColumnTypes.BOOLEAN
            break
          case CustomMetricValueType.PERCENT:
            columnType = FilterColumnTypes.PERCENTAGE
            break
          case CustomMetricValueType.ENUM:
            columnType = FilterColumnTypes.CATEGORICAL
            break
          default:
            columnType = FilterColumnTypes.NUMERICAL
        }
      }
    }
    
    const defaultOperator = getDefaultOperator(columnType)
    
    // Determine table based on column type
    let table: BackendTables
    if (Object.values(SESSION_METRICS_COLUMNS).includes(column as SESSION_METRICS_COLUMNS)) {
      table = BackendTables.SESSION_METRICS
    } else if (Object.values(CallMetricColumns).includes(column as CallMetricColumns)) {
      table = BackendTables.CALL_METRICS
    } else if (isCustomMetricColumn(column)) {
      table = BackendTables.CUSTOM_METRICS
    } else {
      table = BackendTables.PROSPECT_INFO
    }
    
    return {
      table: table,
      column_name: column,
      operator: defaultOperator,
      value_safe: null
    }
  }

  // Find existing filter for this column in the current filter state
  const findExistingFilter = (column: BackendColumns): FilterColumn | null => {
    for (const andGroup of filter) {
      for (const filterCol of andGroup) {
        if (filterCol.column_name === column) {
          return filterCol
        }
      }
    }
    return null
  }

  // Find existing filters for percentage columns (both min GTE and max LTE)
  const findExistingPercentageFilters = (column: BackendColumns): { minFilter: FilterColumn | null, maxFilter: FilterColumn | null } => {
    let minFilter: FilterColumn | null = null;
    let maxFilter: FilterColumn | null = null;

    for (const andGroup of filter) {
      for (const filterCol of andGroup) {
        if (filterCol.column_name === column) {
          if (filterCol.operator === FilterOperator.GE || filterCol.operator === FilterOperator.GT) {
            minFilter = filterCol;
          } else if (filterCol.operator === FilterOperator.LE || filterCol.operator === FilterOperator.LT) {
            maxFilter = filterCol;
          } else if (filterCol.operator === FilterOperator.EQ) {
            // Equal filter counts as both min and max
            minFilter = filterCol;
            maxFilter = filterCol;
          }
        }
      }
    }

    return { minFilter, maxFilter };
  };

  // Update filter for a specific column
  const updateFilterForColumn = (column: BackendColumns, newFilter: FilterColumn) => {
    // Check if this is a percentage column
    const isPercentageColumn = isCustomMetricColumn(column) && 
      getCustomMetricDefinition(column)?.value_type === CustomMetricValueType.PERCENT;
    
    if (isPercentageColumn) {
      // Check if this is a clear all filters case
      if (newFilter.value_safe && 
          typeof newFilter.value_safe === 'object' && 
          newFilter.value_safe.__clearAllFilters === true) {
        
        // Clear all filters for this column
        let newFilterState = filter.map(andGroup => 
          andGroup.filter(filterCol => filterCol.column_name !== column)
        ).filter(andGroup => andGroup.length > 0);
        
        // Dispatch to the appropriate slice based on context
        if (filterContext === FilterContext.ANALYTICS) {
          dispatch(updateAnalyticsFilter(newFilterState));
        } else {
          dispatch(updateHistoryFilter(newFilterState));
        }
        return;
      }
      
      // Check if this is a multiple filters case
      if (newFilter.value_safe && 
          typeof newFilter.value_safe === 'object' && 
          newFilter.value_safe.__multipleFilters === true) {
        
        // Handle multiple filters case - clear all existing filters for this column and add new ones
        let newFilterState = filter.map(andGroup => 
          andGroup.filter(filterCol => filterCol.column_name !== column)
        ).filter(andGroup => andGroup.length > 0);
        
        // Add each new filter as a separate entry
        newFilter.value_safe.filters.forEach((filterToAdd: FilterColumn) => {
          newFilterState.push([filterToAdd]);
        });
        
        // Dispatch to the appropriate slice based on context
        if (filterContext === FilterContext.ANALYTICS) {
          dispatch(updateAnalyticsFilter(newFilterState));
        } else {
          dispatch(updateHistoryFilter(newFilterState));
        }
        return;
      }
      
      // Single filter case - only remove filters with conflicting operators
      // This allows min (GE) and max (LE) filters to coexist
      let operatorsToRemove: FilterOperator[] = [];
      
      if (newFilter.operator === FilterOperator.GE || newFilter.operator === FilterOperator.GT) {
        // Removing min-type operators
        operatorsToRemove = [FilterOperator.GE, FilterOperator.GT];
      } else if (newFilter.operator === FilterOperator.LE || newFilter.operator === FilterOperator.LT) {
        // Removing max-type operators
        operatorsToRemove = [FilterOperator.LE, FilterOperator.LT];
      } else if (newFilter.operator === FilterOperator.EQ) {
        // EQ means exact match, remove all operators for this column
        operatorsToRemove = [FilterOperator.GE, FilterOperator.GT, FilterOperator.LE, FilterOperator.LT, FilterOperator.EQ];
      }
      
      // Remove conflicting filters
      let newFilterState = filter.map(andGroup => 
        andGroup.filter(filterCol => 
          !(filterCol.column_name === column && operatorsToRemove.includes(filterCol.operator))
        )
      ).filter(andGroup => andGroup.length > 0);
      
      // Add the new filter if it has a value
      if (newFilter.value_safe !== null && 
          newFilter.value_safe !== undefined && 
          !(Array.isArray(newFilter.value_safe) && newFilter.value_safe.length === 0)) {
        newFilterState.push([newFilter]);
      }
      
      // Dispatch to the appropriate slice based on context
      if (filterContext === FilterContext.ANALYTICS) {
        dispatch(updateAnalyticsFilter(newFilterState));
      } else {
        dispatch(updateHistoryFilter(newFilterState));
      }
    } else {
      // For non-percentage columns, use the existing logic
      // Remove any existing filters for this column
      let newFilterState = filter.map(andGroup => 
        andGroup.filter(filterCol => filterCol.column_name !== column)
      ).filter(andGroup => andGroup.length > 0);
      
      // Add the new filter if it has a value (not null, undefined, or empty array)
      if (newFilter.value_safe !== null && 
          newFilter.value_safe !== undefined && 
          !(Array.isArray(newFilter.value_safe) && newFilter.value_safe.length === 0)) {
        newFilterState.push([newFilter]);
      }
      
      // Dispatch to the appropriate slice based on context
      if (filterContext === FilterContext.ANALYTICS) {
        dispatch(updateAnalyticsFilter(newFilterState));
      } else {
        dispatch(updateHistoryFilter(newFilterState));
      }
    }
  };

  const renderUserSelect = () => {
    return <FilterSection
    title="Agents"
    icon={<Users className="w-4 h-4" />}
    isExpanded={expandedSections['SELECTED_REPS']}
    onToggle={() => toggleSection('SELECTED_REPS')}
    isActive={hasActiveUserFilters()}
  >
    <MultiUserSelector 
      onSelectedUserOrGroupsChange={(selected: MultiUserSelect[]) => {
        // Expand user groups to their constituent user IDs
        const allUserIds: string[] = []
        
        selected.forEach(selection => {
          if (selection.user_id) {
            // Individual user selected
            allUserIds.push(selection.user_id)
          } else if (selection.user_group_id) {
            // User group selected - expand to all user IDs in that group
            const userGroup = sortedUserGroupInfo.find(g => g.group.user_group_id === selection.user_group_id)
            if (userGroup) {
              allUserIds.push(...userGroup.allUserIds)
            }
          } else if (selection.team_id) {
            // Team selected - get all visible user IDs
            const teamUserIds = allVisibleUsers.map(user => user.user_id)
            allUserIds.push(...teamUserIds)
          }
        })
        
        // Remove duplicates and create filter
        const uniqueUserIds = Array.from(new Set(allUserIds))
        const userFilter: FilterColumn[] = uniqueUserIds.length > 0 ? [{
          table: BackendTables.SESSION_AGENTS,
          column_name: SessionAgentsColumns.CREATOR_AGENT_ID,
          operator: FilterOperator.IN,
          value_safe: uniqueUserIds
        }] : []
        
        // Remove any existing user filters and add new ones
        let newFilterState = filter.map(andGroup => 
          andGroup.filter(filterCol => !(filterCol.table === BackendTables.SESSION_AGENTS && (
            filterCol.column_name === SessionAgentsColumns.CREATOR_AGENT_ID || filterCol.column_name === SessionAgentsColumns.FIRST_USER_ID)))
        ).filter(andGroup => andGroup.length > 0)
        if (userFilter.length > 0) newFilterState.push(userFilter)
        
        // Dispatch to the appropriate slice based on context
        if (filterContext === FilterContext.ANALYTICS) {
          dispatch(updateAnalyticsFilter(newFilterState))
        } else {
          dispatch(updateHistoryFilter(newFilterState))
        }
      }}
      selectedUserOrGroups={selectedReps}
    />
  </FilterSection>
  }


  // Get individual custom metric columns to add to the filter list
  const getCustomMetricColumns = (): BackendColumns[] => {
    if (!special_filters.includes(SPECIAL_FILTERS.CUSTOM_METRICS)) {
      return [];
    }
    
    const filterableCustomMetrics = getFilterableCustomMetrics();
    return filterableCustomMetrics.map(({ columnName }) => columnName as BackendColumns);
  };

  // Get all applicable filters including custom metrics
  const getAllApplicableFilters = (): BackendColumns[] => {
    const baseFilters = applicable_filters || [];
    const customMetricFilters = getCustomMetricColumns();
    return [...baseFilters, ...customMetricFilters];
  };

  const renderColumnFilter = (column: BackendColumns) => {
    // Check if this is a categorical filter and if it has available options
    let columnType = getColumnType(column);
    
    // Override column type for custom metrics based on their definition
    if (isCustomMetricColumn(column)) {
      const definition = getCustomMetricDefinition(column);
      if (definition) {
        switch (definition.value_type) {
          case CustomMetricValueType.BOOLEAN:
            columnType = FilterColumnTypes.BOOLEAN;
            break;
          case CustomMetricValueType.PERCENT:
            columnType = FilterColumnTypes.PERCENTAGE;
            break;
          case CustomMetricValueType.ENUM:
            columnType = FilterColumnTypes.CATEGORICAL;
            break;
          default:
            columnType = FilterColumnTypes.NUMERICAL;
        }
      }
    }
    
    if (columnType === FilterColumnTypes.CATEGORICAL && !hasOptions(column)) {
      return null; // Don't render categorical filters without options
    }
    
    // Handle percentage columns specially
    let existingFilter: FilterColumn;
    if (columnType === FilterColumnTypes.PERCENTAGE) {
      const { minFilter, maxFilter } = findExistingPercentageFilters(column);
      
      // Create a combined filter object that encodes both min and max information
      // We'll store this as a special object in value_safe that the PercentageFilter can decode
      let combinedFilterData: any = {};
      
      if (minFilter) {
        combinedFilterData.min = {
          operator: minFilter.operator,
          value: minFilter.value_safe
        };
      }
      
      if (maxFilter && maxFilter !== minFilter) { // Don't duplicate if EQ filter
        combinedFilterData.max = {
          operator: maxFilter.operator,
          value: maxFilter.value_safe
        };
      }
      
      // Create a filter object with our combined data
      existingFilter = {
        column_name: column,
        table: BackendTables.CUSTOM_METRICS,
        operator: FilterOperator.EQ, // Placeholder operator 
        value_safe: Object.keys(combinedFilterData).length > 0 ? combinedFilterData : null
      };
    } else {
      existingFilter = findExistingFilter(column) || createFilterColumn(column);
    }
    
    // Get the display title for the column
    let title: string;
    if (isCustomMetricColumn(column)) {
      const definition = getCustomMetricDefinition(column);
      title = definition?.label || `Custom Metric ${definition?.metric_idx || ''}`;
    } else {
      title = column.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    
    return (
      <FilterSection
        key={column}
        title={title}
        icon={getIconForColumn(column)}
        isExpanded={expandedSections[column]}
        onToggle={() => toggleSection(column)}
        isActive={hasActiveFilterForColumn(column)}
      >
        <FilterColumnRow
          value={existingFilter}
          onChange={(updatedFilter) => updateFilterForColumn(column, updatedFilter)}
          columnType={columnType}
        />
      </FilterSection>
    );
  };

  const renderFilter = (column: BackendColumns) => {
    const allFilters = getAllApplicableFilters();
    if (!allFilters.includes(column)) return null;
    return renderColumnFilter(column);
  };

  const hasActiveFilters = (): boolean => { return filter.length > 0 }

  // Check if a specific column has active filters
  const hasActiveFilterForColumn = (column: BackendColumns): boolean => {
    return findExistingFilter(column) !== null
  }

  // Check if user/agent filters are active
  const hasActiveUserFilters = (): boolean => {
    return filter.some(andGroup => 
      andGroup.some(filterCol => 
        filterCol.table === BackendTables.SESSION_AGENTS && 
        (filterCol.column_name === SessionAgentsColumns.CREATOR_AGENT_ID || 
         filterCol.column_name === SessionAgentsColumns.FIRST_USER_ID)
      )
    )
  }

  // Check if current filter matches any existing saved view
  const isCurrentFilterSaved = (): boolean => {
    if (!hasActiveFilters()) return false
    
    return savedViews.some(savedView => {
      // Deep compare the filter structures
      return JSON.stringify(filter) === JSON.stringify(savedView.filter)
    })
  }

  return (
    <>
      <div 
        className={`h-full border-r transition-all duration-300 ease-in-out ${
          isVisible ? `w-[220px] max-w-[220px] opacity-100` : 'max-w-0 opacity-0'
        }`}
      >
        {(isVisible || isTransitioning) && (
          <div className="h-full flex flex-col">
            <div className="px-3 py-2 border-b flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900">Filters</h2>
              <div className="flex items-center gap-1">
                {(hasActiveFilters() && !isCurrentFilterSaved()) && (
                  <div className="flex items-center gap-1 mr-1">
                    <button
                      onClick={handleSaveView}
                      className="p-1.5 rounded-md hover:bg-blue-50 transition-colors text-blue-600 group"
                      title="Save current view"
                    >
                      <Save size={16} className="group-hover:scale-110 transition-transform" />
                    </button>
                    <button
                      onClick={() => {
                        if (filterContext === FilterContext.ANALYTICS) {
                          dispatch(updateAnalyticsFilter([]))
                        } else {
                          dispatch(updateHistoryFilter([]))
                        }
                      }}
                      className="p-1.5 rounded-md hover:bg-red-50 transition-colors text-red-600 group"
                      title="Clear all filters"
                    >
                      <X size={16} className="group-hover:scale-110 transition-transform" />
                    </button>
                  </div>
                )}
                {(hasActiveFilters() && isCurrentFilterSaved()) && (
                  <div className="flex items-center gap-1 mr-1">
                    <button
                      onClick={() => {
                        if (filterContext === FilterContext.ANALYTICS) {
                          dispatch(updateAnalyticsFilter([]))
                        } else {
                          dispatch(updateHistoryFilter([]))
                        }
                      }}
                      className="p-1.5 rounded-md hover:bg-red-50 transition-colors text-red-600 group"
                      title="Clear all filters"
                    >
                      <X size={16} className="group-hover:scale-110 transition-transform" />
                    </button>
                  </div>
                )}
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
                  disabled={isTransitioning}
                >
                  <ChevronLeft className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="p-3 space-y-4">
                <div className="space-y-2">
                  {special_filters.includes(SPECIAL_FILTERS.ANALYTICS_DATE) && (
                    <FilterSection
                      title={`Dates`}
                      icon={<Calendar className="w-4 h-4" />}
                      isExpanded={expandedSections[SPECIAL_FILTERS.ANALYTICS_DATE]}
                      onToggle={() => toggleSection(SPECIAL_FILTERS.ANALYTICS_DATE)}
                    >
                      <SimplifiedSingleSelect
                        options={Object.values(DateOptions).map((option) => ({label: option as string, value: option as string}))}
                        value={analyticsDateFilter.dateRange}
                        onChange={(option) => {
                          if (!option) return
                          dispatch(updateAnalyticsDateRange({dateRange: option as DateOptions, startDate: analyticsDateFilter.startDate, endDate: analyticsDateFilter.endDate}))
                        }}
                      />
                      {analyticsDateFilter.dateRange === DateOptions.CUSTOM && (
                        <CalendarComponent
                          start={new Date(analyticsDateFilter.startDate)}
                          end={new Date(analyticsDateFilter.endDate)}
                          onValueUpdate={(startDate, endDate) => dispatch(updateAnalyticsDateRange({dateRange: DateOptions.CUSTOM, startDate: startDate.toISOString(), endDate: endDate.toISOString()}))}
                        />
                      )}
                    </FilterSection>
                  )}
                  {special_filters.includes(SPECIAL_FILTERS.TRANSCRIPT_DATE) && (
                    <FilterSection
                      title={`Dates`}
                      icon={<Calendar className="w-4 h-4" />}
                      isExpanded={expandedSections[SPECIAL_FILTERS.TRANSCRIPT_DATE]}
                      onToggle={() => toggleSection(SPECIAL_FILTERS.TRANSCRIPT_DATE)}
                    >
                      <SimplifiedSingleSelect
                        options={Object.values(TranscriptDateOptions).map((option) => ({label: option as string, value: option as string}))}
                        value={transcriptDateFilter.dateOptions}
                        onChange={(option) => {
                          if (!option) return
                          dispatch(updateTranscriptDateFilter({dateOptions: option as TranscriptDateOptions, dateRange: transcriptDateFilter.dateRange}))
                        }}
                      />
                      {transcriptDateFilter.dateOptions === TranscriptDateOptions.CUSTOM && (
                        <CalendarComponent
                          start={new Date(transcriptDateFilter.dateRange.startDate)}
                          end={new Date(transcriptDateFilter.dateRange.endDate)}
                          onValueUpdate={(startDate, endDate) => dispatch(updateTranscriptDateFilter({dateOptions: TranscriptDateOptions.CUSTOM, originalDateRange: {startDate: startDate.toISOString(), endDate: endDate.toISOString()}, dateRange: {startDate: startDate.toISOString(), endDate: endDate.toISOString()}}))}
                        />
                      )}
                    </FilterSection>
                  )}
                  {special_filters.includes(SPECIAL_FILTERS.SELECTED_REPS) && renderUserSelect()}
                  {getAllApplicableFilters().map((column) => renderFilter(column))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Saved View Modal */}
      <SavedViewModal
        isOpen={isEditingModalOpen}
        onClose={() => dispatch(closeEditingModal())}
        editingView={editingView}
        currentFilter={filter}
      />
    </>
  )
}

interface FilterSectionProps {
  title: string
  icon: React.ReactNode
  isExpanded: boolean
  onToggle: () => void
  children: React.ReactNode
  isActive?: boolean
}

function FilterSection({ title, icon, isExpanded, onToggle, children, isActive = false }: FilterSectionProps) {
  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left group"
      >
        <div className="flex items-center space-x-3">
          <span className="text-gray-400 group-hover:text-gray-500">{icon}</span>
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">{title}</span>
            {isActive && (
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            )}
          </div>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 group-hover:text-gray-500 transform transition-transform duration-300 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>
      <div
        className={`transition-all duration-300 ease-in-out flex flex-col gap-1.5 items-center ${
          isExpanded ? 'max-h-[500px] opacity-100 mt-2 pointer-events-auto' : 'max-h-0 opacity-0 pointer-events-none'
        }`}
      >
        {children}
      </div>
    </div>
  )
}